import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupee } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { ImageUploader } from "@/components/ImageUploader";

const BLANK = { name: "", qualification: "", specialization: "", experience: 0, fee: 0, image: "", languages: [], mode: "", bio: "", areas: [] };

export default function AdminConsultants() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/consultants").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = () => {
    const payload = { ...editing, experience: Number(editing.experience), fee: Number(editing.fee) };
    const req = editing.id ? api.put(`/admin/consultants/${editing.id}`, payload) : api.post("/admin/consultants", payload);
    req.then(() => { toast.success("Saved"); setEditing(null); load(); });
  };
  const del = (id) => { if (!window.confirm("Remove consultant?")) return; api.delete(`/admin/consultants/${id}`).then(() => { toast.success("Removed"); load(); }); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Consultants</h1>
        <Button onClick={() => setEditing({ ...BLANK })} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="add-consultant-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      {!list ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid={`admin-consultant-${c.id}`}>
              <div className="aspect-[3/2] bg-sand overflow-hidden"><img src={c.image} alt={c.name} className="w-full h-full object-cover" /></div>
              <div className="p-4">
                <h3 className="font-heading text-xl text-forest">{c.name}</h3>
                <p className="text-sm text-olive">{c.specialization} • {rupee(c.fee)}</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...BLANK, ...c })} className="rounded-full flex-1 border-sage"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  <button onClick={() => del(c.id)} className="w-9 h-9 rounded-full border border-sage flex items-center justify-center"><Trash2 className="w-4 h-4 text-terracotta" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">{editing?.id ? "Edit" : "Add"} Consultant</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-sm">Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-xl bg-white" data-testid="csf-name" /></div>
              <div><Label className="text-sm">Specialization</Label><Input value={editing.specialization} onChange={(e) => setEditing({ ...editing, specialization: e.target.value })} className="rounded-xl bg-white" /></div>
              <div><Label className="text-sm">Qualification</Label><Input value={editing.qualification} onChange={(e) => setEditing({ ...editing, qualification: e.target.value })} className="rounded-xl bg-white" /></div>
              <div><Label className="text-sm">Experience (yrs)</Label><Input type="number" value={editing.experience} onChange={(e) => setEditing({ ...editing, experience: e.target.value })} className="rounded-xl bg-white" /></div>
              <div><Label className="text-sm">Fee (₹)</Label><Input type="number" value={editing.fee} onChange={(e) => setEditing({ ...editing, fee: e.target.value })} className="rounded-xl bg-white" /></div>
              <div><Label className="text-sm">Mode</Label><Input value={editing.mode} onChange={(e) => setEditing({ ...editing, mode: e.target.value })} className="rounded-xl bg-white" /></div>
              <div><Label className="text-sm">Languages (comma)</Label><Input value={editing.languages?.join(", ")} onChange={(e) => setEditing({ ...editing, languages: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="rounded-xl bg-white" /></div>
              <div className="col-span-2"><Label className="text-sm mb-1.5 block">Photo</Label><ImageUploader value={editing.image} onChange={(url) => setEditing((cur) => ({ ...cur, image: url }))} label="Upload consultant photo" /></div>
              <div className="col-span-2"><Label className="text-sm">Bio</Label><Textarea value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} className="rounded-xl bg-white" rows={3} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="save-consultant-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
